import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'components/shared/Modal';
import ModalFooter from 'components/shared/modal/ModalFooter';
import { ModalRouteProps } from 'context/ModalRouter';
import { Alert, Button, Icon } from '@kontentino/ui';
import { faUserPlus } from '@fortawesome/pro-regular-svg-icons/faUserPlus';
import InviteUserForm, { InviteUserFormState } from './InviteUserForm';
import InviteUserSuccessModal from './InviteUserSuccessModal';
import { InviteUserResponse } from 'types/User';
import { useTranslation } from 'react-i18next';
import { useSeatLimitErrorHandler } from 'app/utils/useSeatLimitErrorHandler';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';
import { modalRoutes } from 'constants/routes';
import { getFieldError } from 'app/utils/apiError';
import { invitationErrorMessage } from 'app/utils/invitationErrors';
import { useInviteUserMutation } from './useInviteUserMutation';
import { parseInviteModalParams } from './inviteModalParams';

const InviteUserModal = (props: ModalRouteProps) => {
  const { modalManager, modalHistory } = props;
  const [isFormValid, setIsFormValid] = useState(false);
  const [inviteResponse, setInviteResponse] =
    useState<InviteUserResponse | null>(null);
  const { t } = useTranslation();
  const handleSeatLimitError = useSeatLimitErrorHandler();
  const { hasReachedUsersLimit, userSeats } = useSubscriptionInfo();
  const isAtSeatLimit = hasReachedUsersLimit();
  const hasRedirectedToLimitModalRef = useRef(false);
  const [hasConfirmedBelowLimit, setHasConfirmedBelowLimit] = useState(false);
  const [{ profileId, role: initialRole }] = useState(() =>
    parseInviteModalParams(modalManager.getQueryParameter),
  );
  const initialProfileIds = useMemo(
    () => (profileId ? [profileId] : []),
    [profileId],
  );

  useEffect(() => {
    if (hasRedirectedToLimitModalRef.current) return;
    if (hasConfirmedBelowLimit) return;
    if (!userSeats) return;

    if (isAtSeatLimit) {
      hasRedirectedToLimitModalRef.current = true;
      modalHistory.replaceLast({
        hash: modalRoutes.USER_LIMIT_REACHED,
      });
      modalManager.destroy();
    } else {
      setHasConfirmedBelowLimit(true);
    }
  }, [
    isAtSeatLimit,
    userSeats,
    modalHistory,
    modalManager,
    hasConfirmedBelowLimit,
  ]);

  const sendInvitationMutation = useInviteUserMutation({
    onInvited: setInviteResponse,
    onSeatLimitError: handleSeatLimitError,
  });

  function onSubmit(values: InviteUserFormState) {
    sendInvitationMutation.mutate(values);
  }

  const inviteError = sendInvitationMutation.error;
  const emailFieldError = getFieldError(inviteError, 'email');

  const handleSuccessModalClose = () => {
    setInviteResponse(null);
    modalManager.close();
  };

  if (isAtSeatLimit && !hasConfirmedBelowLimit) {
    return null;
  }

  if (inviteResponse) {
    return (
      <InviteUserSuccessModal
        isOpen={true}
        onClose={handleSuccessModalClose}
        inviteResponse={inviteResponse}
        initialProfileIds={initialProfileIds}
      />
    );
  }

  return (
    <Modal
      modalManager={modalManager}
      size="sm"
      dataName={{
        wrapper: 'invite-user-modal',
      }}
    >
      <Modal.Header
        title={t('inviteUserModal.inviteTeamMemberTitle')}
        className="tw-pb-0"
      />
      <Modal.Content className="tw-py-4">
        {sendInvitationMutation.isError && !emailFieldError && (
          <Alert variant="danger" className="tw-mb-4">
            {invitationErrorMessage(
              inviteError,
              t,
              'pending',
              'inviteUserModal.somethingWentWrong',
            )}
          </Alert>
        )}

        <InviteUserForm
          onSubmit={onSubmit}
          isSubmitting={sendInvitationMutation.isLoading}
          onValidityChange={setIsFormValid}
          serverError={emailFieldError ?? ''}
          initialRole={initialRole ?? undefined}
        />
      </Modal.Content>
      <ModalFooter withDivider className="tw-flex tw-justify-center">
        <Button
          type="submit"
          form="invite-user-form"
          disabled={sendInvitationMutation.isLoading || !isFormValid}
          iconBefore={<Icon icon={faUserPlus} />}
          isLoading={sendInvitationMutation.isLoading}
          className="tw-w-full"
        >
          {t('inviteUserModal.inviteTeamMemberButton')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default InviteUserModal;
